import type {
  ItemsReplacementReq,
  ReplaceMultipleItemsReq,
  ReplaceSingleItemReq,
  SuggestionsWithAIReq,
} from './substitutionFlow.request';
import type {
  ItemsReplacementRawResp,
  SuggestionsWithAIRawResp,
} from './substitutionFlow.response';
import type {
  GetItemsReplacementParams,
  ItemsReplacementModel,
  ToReplaceMultipleItemsData,
  ToReplaceSingleItemData,
  GetSuggestionsWithAIParams,
} from 'features/order/substitution-flow/domain';

export function toReadItemsReplacement(data: GetItemsReplacementParams) {
  const result: ItemsReplacementReq = {
    payload: {
      name: data.query,
      order_id: data.orderId,
      is_product: data.isProduct,
      product_id: data.productId,
      topping_id: data.toppingId ?? null,
    },
  };
  return result;
}

export function toReadSuggestionsWithAI(data: GetSuggestionsWithAIParams) {
  const result: SuggestionsWithAIReq = {
    payload: {
      order_id: data.orderId,
      is_product: data.isProduct,
      product_id: data.productId,
      topping_id: data.toppingId ?? null,
    },
  };
  return result;
}

export function toItemsReplacement(data: ItemsReplacementRawResp) {
  const imgHost = window.localStorage.getItem('countryImagesS3') ?? '';
  const result: ItemsReplacementModel = {
    products: data.products.map(p => ({
      id: p.product_id,
      name: p.name,
      description: p.description ?? '',
      image: `${imgHost}/products/${p.image ?? ''}`,
      storeId: p.store_id,
      price: {
        base: p.price ?? 0,
        real: p.real_price ?? 0,
      },
    })),
    toppings:
      data.toppings?.toppings.map(t => ({
        id: t.id,
        categoryId: t?.topping_category_id ?? 0,
        categoryName: data.toppings?.topping_category_description ?? '',
        name: t.description ?? '',
        image: `${imgHost}/products/${t.image ?? ''}`,
        price: {
          base: t.price ?? 0,
          real: t.price ?? 0,
        },
      })) ?? [],
  };
  return result;
}

export function toSuggestionsWithAI(data: SuggestionsWithAIRawResp) {
  return toItemsReplacement({
    products: data.products,
    toppings: {
      topping_category_description:
        data.toppings?.[0]?.topping_category_description ?? '',
      topping_category_index: data.toppings?.[0]?.topping_category_index ?? 0,
      toppings: data.toppings,
    },
  });
}

export function toReplaceSingleItem(
  data: ToReplaceSingleItemData,
): ReplaceSingleItemReq {
  const { order, partnerId, itemsOutStock, suggestions } = data;
  return {
    payload: {
      order_id: order.id,
      partner_id: partnerId,
      store_id: order.products[0].storeId,
      initial_modification: {
        order_id: order.id,
        partner_id: partnerId,
        state: 'to_wait',
        user_name: order.user.name.full,
        products: itemsOutStock.products.map(product => ({
          type: 'substitution',
          product_id: product.id,
          order_product_id: product.orderProductId,
          name: product.name,
          description: product.description,
          image: product.image,
          units: product.units,
          price: product.price.unitReal,
          total: product.price.totalReal,
          replacement_product_options: suggestions.products.map(s => ({
            accepted: false,
            added_product_id: s.id,
            added_product_name: s.name,
            added_product_description: s.description,
            added_product_image: s.image,
            added_product_units: product.units,
            added_product_price: s.price.real,
            added_product_total: s.price.real * product.units,
          })),
        })),
        toppings: itemsOutStock.toppings.map(topping => ({
          type: 'substitution',
          topping_id: topping.detail.id,
          product_id: topping.productId,
          order_product_id: topping.orderProductId,
          topping_description: topping.detail.description,
          image: '',
          units: topping.detail.units,
          price: topping.detail.price.real,
          is_removable_topping: true,
          product_name: topping.productName,
          topping_category_id: suggestions.toppings?.[0]?.categoryId ?? 0,
          topping_category_description: topping.detail.name,
          replacement_topping_options: suggestions.toppings.map(s => ({
            accepted: false,
            added_topping_id: s.id,
            added_topping_description: s.name,
            added_topping_image: s.image,
            added_topping_units: topping.detail.units,
            added_topping_price: s.price.real,
            added_topping_total: s.price.real * topping.detail.units,
          })),
        })),
      },
    },
  };
}

export function toReplaceMultipleItems(
  data: ToReplaceMultipleItemsData,
): ReplaceMultipleItemsReq {
  return {
    payload: {
      order_id: data.order.id,
      partner_id: data.partnerId,
      store_id: data.order.products[0].storeId,
      flow_type: 'MULTI_ITEM_SUBSTITUTION_FLOW',
      product_substitution_details: data.itemsOutStock.products.map(p => ({
        replaced_product_details: {
          order_product_id: p.orderProductId,
          product_id: p.id,
          product_name: p.name,
          product_description: p.description,
          image: p.image,
          units: p.units,
        },
      })),
      topping_substitution_details: data.itemsOutStock.toppings.map(t => ({
        replaced_topping_details: {
          order_product_id: t.orderProductId,
          product_id: t.productId,
          product_name: t.productName,
          topping_id: t.detail.id,
          topping_category_id: 0,
          topping_category_description: t.detail.name,
          topping_description: t.detail.description,
          image: '',
          units: t.detail.units,
        },
      })),
    },
  };
}

export const substitutionFlowAdapter = {
  toReadItemsReplacement,
  toReadSuggestionsWithAI,
  toItemsReplacement,
  toSuggestionsWithAI,
  toReplaceSingleItem,
  toReplaceMultipleItems,
};
